import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Badge } from '@shared/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';

/**
 * Site Management Component
 * Manage up to 3 sites for the organization
 * Part of Phase 1 Quick Wins
 */
const SiteManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  
  const MAX_SITES = 3;

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
  });

  useEffect(() => {
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadSites = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('depots')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sites',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (site = null) => {
    if (site) {
      setEditingSite(site);
      setFormData({
        name: site.name || '',
        code: site.code || '',
        address: site.address || '',
      });
    } else {
      setEditingSite(null);
      setFormData({
        name: '',
        code: '',
        address: '',
      });
    }
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingSite(null);
    setFormData({
      name: '',
      code: '',
      address: '',
    });
  };

  const handleSaveSite = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Site name is required',
        variant: 'destructive',
      });
      return;
    }

    // Auto-generate code from name if not editing
    const siteCode = editingSite 
      ? formData.code 
      : formData.name.toUpperCase().replace(/[^A-Z0-9]/g, '-').substring(0, 20);

    try {
      if (editingSite) {
        // Update existing site
        const { error } = await supabase
          .from('depots')
          .update({
            name: formData.name,
            address: formData.address,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingSite.id);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Site updated successfully',
        });
      } else {
        // Create new site
        const { error } = await supabase
          .from('depots')
          .insert([{
            name: formData.name,
            code: siteCode,
            address: formData.address,
          }]);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Site created successfully',
        });
      }

      handleCloseModal();
      loadSites();
    } catch (error) {
      console.error('Error saving site:', error);
      toast({
        title: 'Error',
        description: `Failed to ${editingSite ? 'update' : 'create'} site`,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSite = async (site) => {
    if (!confirm(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('depots')
        .delete()
        .eq('id', site.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Site deleted successfully',
      });

      loadSites();
    } catch (error) {
      console.error('Error deleting site:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete site. It may have associated data.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Site Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage up to {MAX_SITES} sites for your organization
          </p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          disabled={sites.length >= MAX_SITES}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Site {sites.length >= MAX_SITES && '(Max Reached)'}
        </Button>
      </div>

      {/* Limit Warning */}
      {sites.length >= MAX_SITES && (
        <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              Site Limit Reached
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              You have reached the maximum of {MAX_SITES} sites. Delete an existing site to add a new one.
            </p>
          </div>
        </div>
      )}

      {/* Sites Grid */}
      {sites.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No sites configured</h3>
              <p className="text-muted-foreground mt-2">
                Add your first site to get started
              </p>
              <Button onClick={() => handleOpenModal()} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add Site
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((site, index) => (
            <Card key={site.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      {site.name}
                    </CardTitle>
                    <Badge variant="outline" className="mt-2">
                      Site {index + 1} of {MAX_SITES}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address */}
                <div className="space-y-1 text-sm">
                  {site.address ? (
                    <p className="text-foreground">{site.address}</p>
                  ) : (
                    <p className="text-muted-foreground italic">No address provided</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenModal(site)}
                    className="flex-1 flex items-center gap-2"
                  >
                    <Edit className="h-3 w-3" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteSite(site)}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Site Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSite ? 'Edit Site' : 'Add New Site'}
            </DialogTitle>
            <DialogDescription>
              {editingSite 
                ? 'Update the site details below'
                : `Add a new site (${sites.length + 1} of ${MAX_SITES})`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="site-name">Site Name *</Label>
              <Input
                id="site-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Main Depot"
              />
            </div>

            <div>
              <Label htmlFor="site-address">Address</Label>
              <Input
                id="site-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street, London, SW1A 1AA"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleSaveSite} disabled={!formData.name.trim()}>
              {editingSite ? 'Update Site' : 'Add Site'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteManagement;
